// BaseMapDataOverlaysTab.vue
<template>
  <div>
    <!-- Keeps the hazard markers on the page's map and owns their details panel. Lives here
         because this tab is always rendered, visited or not; the markers' on/off switch is
         the first row of the Maps tab's More view (HazardsSwitchRow). -->
    <HazardsMapHost v-if="showHazards" :ignore-click="isAddingWaypoint" />
    <!-- ADMIN ONLY: keeps the "Public photos" layer on the map and owns the photo
         dialog its dots open. Not mounted at all for anyone else; the /vector
         proxy and the adminPublicPhotos query refuse non-admins regardless. -->
    <PublicPhotosMapHost v-if="isAdmin" :ignore-click="isAddingWaypoint" />
    <!-- ───────────────────────── DATA OVERLAYS ───────────────────────── -->
    <div class="row">
      <div class="col-12 px-0 pt-3 py-2">
        <p class="px-3 mb-0"><small><strong>{{ $t('base_map_dialog.base_map_data_overlays_tab.data_overlays') }}</strong></small></p>
        <div ref="overlayScroll" class="mapstyle-list">

          <div class="position-relative d-inline-block" @click="toggleOverlay('toggleHeatmapOverlay')">
            <div class="mapstyle-card" :class="{ 'mapstyle-card-active': heatmapActive }">
              <img src="../../../images/map_style/heatmap_overlay_style.webp" :alt="$t('base_map_dialog.base_map_data_overlays_tab.heatmap_2')" draggable="false" />
              <!-- Free users get the overlay but capped at z12; PRO+ unlocks native z15 trail detail. -->
              <img v-if="!heatmapIsPro" :src="proBadge" class="mapstyle-pro-badge" :alt="$t('base_map_dialog.base_map_data_overlays_tab.trail_detail_is_a_pro_feature')" />
              <div class="hiking-text p-1"><p class="m-0"><small><strong>{{ $t('base_map_dialog.base_map_data_overlays_tab.heatmap') }}</strong></small></p></div>
            </div>
          </div>

          <div class="position-relative d-inline-block" @click="toggleOverlay('toggleSlopeOverlay')">
            <div class="mapstyle-card" :class="{ 'mapstyle-card-active': slopeAngleActive }">
              <img src="../../../images/map_style/slope_overlay_style.webp" :alt="$t('shared.map')" draggable="false" />
              <div class="hiking-text p-1"><p class="m-0"><small><strong>{{ $t('base_map_dialog.base_map_data_overlays_tab.slope_angle') }}</strong></small></p></div>
            </div>
          </div>

          <div class="position-relative d-inline-block" @click="toggleOverlay('toggleCellCoverageOverlay')">
            <div class="mapstyle-card" :class="{ 'mapstyle-card-active': cellCoverageActive }">
              <img src="../../../images/map_style/cell_overlay_style.webp" :alt="$t('shared.map')" draggable="false" />
              <div class="hiking-text p-1"><p class="m-0"><small><strong>{{ $t('base_map_dialog.base_map_data_overlays_tab.cell_coverage') }}</strong></small></p></div>
            </div>
          </div>

          <div class="position-relative d-inline-block" @click="toggleOverlay('toggleOfficialHikesOverlay')">
            <div class="mapstyle-card" :class="{ 'mapstyle-card-active': officialHikesActive }">
              <img src="../../../images/map_style/trails_overlay_style.webp" :alt="$t('shared.map')" draggable="false" />
              <button
                type="button"
                class="mapstyle-line-style-btn"
                :title="$t('base_map_dialog.base_map_data_overlays_tab.customise_line_style')"
                :aria-label="$t('base_map_dialog.base_map_data_overlays_tab.customise_hiiker_hikes_line_style')"
                @click.stop="$emit('customise-overlay-line', 'officialHikes')"
              >
                <span class="material-icons">settings</span>
              </button>
              <div class="hiking-text p-1"><p class="m-0"><small><strong>{{ $t('base_map_dialog.base_map_data_overlays_tab.hiiker_hikes') }}</strong></small></p></div>
            </div>
          </div>

          <div class="position-relative d-inline-block" @click="toggleOverlay('toggleLandAccessOverlay')">
            <div class="mapstyle-card" :class="{ 'mapstyle-card-active': landAccessActive }">
              <img src="../../../images/map_style/public_overlay_style.webp" :alt="$t('shared.map')" draggable="false" />
              <div class="hiking-text p-1"><p class="m-0"><small><strong>{{ $t('base_map_dialog.base_map_data_overlays_tab.public_access') }}</strong></small></p></div>
            </div>
          </div>

          <!-- ADMIN ONLY: every public, geotagged user photo (utils/publicPhotos). -->
          <div v-if="isAdmin" class="position-relative d-inline-block" data-test="public-photos-card" @click="togglePublicPhotos">
            <div class="mapstyle-card" :class="{ 'mapstyle-card-active': publicPhotosActive }">
              <div class="la-overlay-thumb public-photos-thumb"><span class="material-icons">photo_camera</span></div>
              <div class="hiking-text p-1"><p class="m-0"><small><strong>{{ $t('admin_public_photos.overlay_title') }}</strong></small></p></div>
            </div>
          </div>

          <div class="position-relative d-inline-block" @click="toggleOverlay('toggleCyclingRoutesOverlay')">
            <div class="mapstyle-card" :class="{ 'mapstyle-card-active': cyclingRoutesActive }">
              <img src="../../../images/map_style/cycling_overlay_style.webp" :alt="$t('base_map_dialog.base_map_data_overlays_tab.cycling_routes_2')" draggable="false" />
              <div class="hiking-text p-1"><p class="m-0"><small><strong>{{ $t('base_map_dialog.base_map_data_overlays_tab.cycling_routes') }}</strong></small></p></div>
            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- ───────────────────────── WEATHER MAPS ───────────────────────── -->
    <div class="row">
      <div class="col-12 px-0 py-1">
        <p class="px-3 mb-0"><small><strong>{{ $t('base_map_dialog.base_map_data_overlays_tab.weather_maps') }}</strong></small></p>
        <div ref="weatherScroll" class="mapstyle-list">

          <div class="position-relative d-inline-block" @click="toggleOverlay('toggleWeatherOverlay', {type:'TA2',name:'temperature'})">
            <div class="mapstyle-card" :class="{ 'mapstyle-card-active': activeWeatherLayer.name === 'temperature' }">
              <img src="../../../images/map_style/temperature_weather_style.webp" :alt="$t('base_map_dialog.base_map_data_overlays_tab.temperature_map')" draggable="false" />
              <div class="hiking-text p-1"><p class="m-0"><small><strong>{{ $t('base_map_dialog.base_map_data_overlays_tab.temperature') }}</strong></small></p></div>
            </div>
          </div>

          <div class="position-relative d-inline-block" @click="toggleOverlay('toggleWeatherOverlay', {type:'CL',name:'cloud'})">
            <div class="mapstyle-card" :class="{ 'mapstyle-card-active': activeWeatherLayer.name === 'cloud' }">
              <img src="../../../images/map_style/cloud_weather_style.webp" :alt="$t('base_map_dialog.base_map_data_overlays_tab.cloud_map')" draggable="false" />
              <div class="hiking-text p-1"><p class="m-0"><small><strong>{{ $t('base_map_dialog.base_map_data_overlays_tab.cloud') }}</strong></small></p></div>
            </div>
          </div>

          <div class="position-relative d-inline-block" @click="toggleOverlay('toggleWeatherOverlay', {type:'PAR0',name:'rainfall'})">
            <div class="mapstyle-card" :class="{ 'mapstyle-card-active': activeWeatherLayer.name === 'rainfall' }">
              <img src="../../../images/map_style/rainfall_weather_style.webp" :alt="$t('base_map_dialog.base_map_data_overlays_tab.rainfall_map')" draggable="false" />
              <div class="hiking-text p-1"><p class="m-0"><small><strong>{{ $t('base_map_dialog.base_map_data_overlays_tab.rainfall') }}</strong></small></p></div>
            </div>
          </div>

          <div class="position-relative d-inline-block" @click="toggleOverlay('toggleWeatherOverlay', {type:'PAS0',name:'snowfall'})">
            <div class="mapstyle-card" :class="{ 'mapstyle-card-active': activeWeatherLayer.name === 'snowfall' }">
              <img src="../../../images/map_style/snowfall_weather_style.webp" :alt="$t('base_map_dialog.base_map_data_overlays_tab.snowfall_map')" draggable="false" />
              <div class="hiking-text p-1"><p class="m-0"><small><strong>{{ $t('base_map_dialog.base_map_data_overlays_tab.snowfall') }}</strong></small></p></div>
            </div>
          </div>

          <div class="position-relative d-inline-block" @click="toggleOverlay('toggleWeatherOverlay', {type:'WND',name:'wind'})">
            <div class="mapstyle-card" :class="{ 'mapstyle-card-active': activeWeatherLayer.name === 'wind' }">
              <img src="../../../images/map_style/wind_weather_style.webp" :alt="$t('base_map_dialog.base_map_data_overlays_tab.wind_map')" draggable="false" />
              <div class="hiking-text p-1"><p class="m-0"><small><strong>{{ $t('base_map_dialog.base_map_data_overlays_tab.wind') }}</strong></small></p></div>
            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- ───────────────────────── MY TRAILS ───────────────────────── -->
    <div class="row">
      <div class="col-12 px-0 py-1">
        <p class="px-3 mb-0"><small><strong>{{ $t('base_map_dialog.base_map_data_overlays_tab.my_trails') }}</strong></small></p>
        <div ref="trailsScroll" class="mapstyle-list">

          <div
            v-for="(label, source) in trailOptions"
            :key="source"
            class="position-relative d-inline-block"
            @click="toggleOverlay('toggleTrail', source)"
          >
            <div class="mapstyle-card" :class="{ 'mapstyle-card-active': isSourceActive(source) }">
              <img :src="getTrailImage(source)" :alt="$t('shared.map')" draggable="false" />
              <button
                v-if="overlayLineTypeForSource(source)"
                type="button"
                class="mapstyle-line-style-btn"
                :title="$t('base_map_dialog.base_map_data_overlays_tab.customise_line_style_2')"
                :aria-label="$t('base_map_dialog.base_map_data_overlays_tab.customise_named_line_style', { label })"
                @click.stop="$emit('customise-overlay-line', overlayLineTypeForSource(source))"
              >
                <span class="material-icons">settings</span>
              </button>
              <div class="hiking-text p-1"><p class="m-0"><small><strong>{{ label }}</strong></small></p></div>
            </div>
          </div>

          <!-- Hike lists: the card turns every list on/off; the cog opens the list picker -->
          <div class="position-relative d-inline-block hike-lists-card" @click="toggleOverlay('toggleHikeListsOverlay')">
            <div class="mapstyle-card" :class="{ 'mapstyle-card-active': hikeListsActive }">
              <img :src="hikeListsImage" :alt="$t('shared.map')" draggable="false" />
              <button
                type="button"
                class="mapstyle-line-style-btn"
                :title="$t('base_map_dialog.base_map_data_overlays_tab.choose_hike_lists')"
                :aria-label="$t('base_map_dialog.base_map_data_overlays_tab.choose_hike_lists')"
                @click.stop="$emit('open-hike-lists')"
              >
                <span class="material-icons">settings</span>
              </button>
              <div class="hiking-text p-1"><p class="m-0"><small><strong>{{ $t('base_map_dialog.base_map_data_overlays_tab.hike_lists') }}</strong></small></p></div>
            </div>
          </div>

        </div>
      </div>
    </div>

  </div>
</template>

<script lang="ts">
import { defineComponent, nextTick } from 'vue';
import { mapState, mapGetters, mapActions } from 'vuex';

// Images that are strictly presentation-layer resources stay here
import plannedImage     from '../../../images/map_style/planned_vector_style.webp';
import recordingsImage  from '../../../images/map_style/recordings_vector_style.webp';
import completedImage   from '../../../images/map_style/completed_vector_style.webp';
import hikeListsImage   from '../../../images/map_style/hike_lists_vector_style.webp';
import proBadge         from '../../../images/PRO-PLUS_BADGE.svg';
import { RevenueCatWrapper } from '../../../utils/RevenueCatWrapper';
import { overlayLineTypeForSource } from '../../../utils/overlayLineStyles';
import { overlaySelection, trackOverlayToggle } from '../../../utils/overlayToggleAnalytics';
import HazardsMapHost from '../../trail_hazards/HazardsMapHost.vue';
import PublicPhotosMapHost from '../../public_photos/PublicPhotosMapHost.vue';
import emitter from '../../../utils/bus';
import { isAdminViewer } from '../../../utils/publicPhotos/publicPhotosLayerSpec';
import { PUBLIC_PHOTOS_VISIBILITY_EVENT, sharedPublicPhotosLayer } from '../../../utils/publicPhotos/publicPhotosLayerController';

export default defineComponent({
  name: 'TrailOptions',

  components: { HazardsMapHost, PublicPhotosMapHost },

  props: {
    // Off for maps that shouldn't carry hazard markers (the 3D peak preview): no host.
    showHazards: { type: Boolean, default: true },
  },

  emits: ['customise-overlay-line', 'open-hike-lists'],

  data() {
    return {
      proBadge,
      hikeListsImage,
      // Whether the current user is PRO+/PRO. Resolved async on mount; drives
      // the badge and the z15 unlock in addHeatmap (see the store toggle).
      heatmapIsPro: false,
      // ADMIN ONLY "Public photos" overlay: whether this viewer gets the card
      // (cosmetic — the server is the gate) and the shared layer's switch.
      isAdmin: isAdminViewer((window as any).user, (window as any).admin),
      publicPhotosActive: sharedPublicPhotosLayer().isEnabled(),
    };
  },

  computed: {
    ...mapState('mapDataOverlays', [
      /* presentational */
      'trailOptions',
      /* overlay flags – now owned by the store */
      'officialHikesActive',
      'cellCoverageActive',
      'slopeAngleActive',
      'landAccessActive',
      'activeWeatherLayer',
      'cyclingRoutesActive',
      'heatmapActive',
      'hikeListsActive'
    ]),
    ...mapGetters('mapDataOverlays', ['isSourceActive']),
  },

  watch: {
    /* ─────────── store actions ─────────── */
    officialHikesActive: function (newVal: boolean) {
      console.log("HiiKER trails active: ", newVal);
    },
    cellCoverageActive: function (newVal: boolean) {
      console.log("Cell coverage active: ", newVal);
    },
    slopeAngleActive: function (newVal: boolean) {
      console.log("Slope angle active: ", newVal);
    },
    activeWeatherLayer: function (newVal: any) {
      console.log("Active weather layer: ", newVal);
    }
  },

  methods: {
    /* ─────────── store actions ─────────── */
    ...mapActions('mapDataOverlays', [
      'initOverlayListeners',
      'initActiveSources',
      'destroyOverlayListeners'
    ]),

    // Every card goes through here so a tap is tracked as overlay_added /
    // overlay_removed. Tracked here rather than in the store actions because
    // "On this map" reuses those actions to bulk-clear and undo, which aren't
    // the user picking an overlay. The toggles commit synchronously before any
    // await, so `after` is read straight after dispatch: awaiting would let a
    // quick second tap (heatmap's tier check) land in between and be missed.
    toggleOverlay(action: string, payload?: unknown) {
      const before = overlaySelection(this.$store.state.mapDataOverlays);
      const done = this.$store.dispatch(`mapDataOverlays/${action}`, payload);
      trackOverlayToggle(before, overlaySelection(this.$store.state.mapDataOverlays));
      return done;
    },

    // While a waypoint is being placed a click drops it; a hazard marker under
    // the cursor doesn't open its panel then (the same rule as hike highlights).
    isAddingWaypoint() {
      return !!(this.$store?.state as any)?.waypoints?.waypointAddingModeEnabled;
    },
    // Straight to the shared layer, not through toggleOverlay: it isn't one of
    // the store's overlays, and an admin tool isn't product analytics.
    togglePublicPhotos() {
      sharedPublicPhotosLayer().setEnabled(!this.publicPhotosActive);
    },
    onPublicPhotosVisibility(enabled: unknown) {
      this.publicPhotosActive = !!enabled;
    },
    // Signed in or out after the page rendered (authSync replaces window.user).
    onUserUpdated() {
      this.isAdmin = isAdminViewer((window as any).user, (window as any).admin);
      sharedPublicPhotosLayer().sync();
    },

    /* ─────────── purely UI helpers ─────────── */
    // Only the line-drawing overlays get a line-style gear.
    overlayLineTypeForSource,

    getTrailImage(source: string) {
      switch (source) {
        case 'get_user_recordings':       return recordingsImage;
        case 'get_user_planned_trails':   return plannedImage;
        case 'get_user_completed_hikes': return completedImage;
        default:                          return plannedImage;
      }
    },

    makeDraggable(el: HTMLElement) {
      /* unchanged – provides swipe-scroll for the card lists */
      let isDown = false, startX = 0, scrollLeft = 0, moved = false;
      el.addEventListener('dragstart', e => e.preventDefault());
      el.addEventListener('mousedown',  e => { isDown = true; moved = false; el.classList.add('dragging');
        startX = e.pageX - el.offsetLeft; scrollLeft = el.scrollLeft; });
      el.addEventListener('mousemove',  e => { if (!isDown) return;
        const x = e.pageX - el.offsetLeft;
        if (Math.abs(x - startX) > 5) moved = true;
        el.scrollLeft = scrollLeft - (x - startX) * 1.5; });
      ['mouseup','mouseleave'].forEach(evt => el.addEventListener(evt, () => { isDown = false; el.classList.remove('dragging'); }));
      el.addEventListener('click', e => { if (moved) e.stopImmediatePropagation(); }, true);
    }
  },

  mounted() {
    emitter.on(PUBLIC_PHOTOS_VISIBILITY_EVENT, this.onPublicPhotosVisibility);
    emitter.on('user:updated', this.onUserUpdated);
    this.initOverlayListeners();     // subscribe to bus + restore persisted overlays
    RevenueCatWrapper.isProOrProPlus().then(v => { this.heatmapIsPro = v; }).catch(() => {});
    nextTick(() => {
      [this.$refs.overlayScroll, this.$refs.weatherScroll, this.$refs.trailsScroll]
        .forEach((el: any) => el && this.makeDraggable(el));
    });
  },

  beforeUnmount() {
    emitter.off(PUBLIC_PHOTOS_VISIBILITY_EVENT, this.onPublicPhotosVisibility);
    emitter.off('user:updated', this.onUserUpdated);
    this.destroyOverlayListeners();  // unsubscribe + clean map layers
  }
});
</script>

<style scoped>
/* ───── (identical presentation styles – no logic here) ───── */
.mapstyle-list{display:flex;overflow-x:auto;max-width:100%;padding-bottom:1em;}
.mapstyle-list::-webkit-scrollbar{display:none}
.mapstyle-list>.position-relative{flex:0 0 auto;}
.mapstyle-card{position:relative;width:120px;background:#fff;border:2px solid #434343;border-radius:6px;cursor:pointer;margin-top:1em;margin-right:1em;transition:border-color .3s ease;overflow:hidden}
.mapstyle-card-active{border-color:#fddf00}
.mapstyle-card img{width:100%}
.mapstyle-card .hiking-text{position:absolute;left:0;bottom:0;width:100%;background:rgba(0,0,0,.7);color:#fff}
.mapstyle-card img.mapstyle-pro-badge{position:absolute;top:4px;right:4px;width:auto;height:22px;z-index:2;filter:drop-shadow(0 1px 2px rgba(0,0,0,.5))}
/* Line-style gear, top-right of the card. Stops the click from also toggling
   the overlay (see @click.stop in the template). */
.mapstyle-line-style-btn{position:absolute;top:4px;right:4px;z-index:3;display:flex;align-items:center;justify-content:center;width:24px;height:24px;padding:0;border:none;border-radius:50%;background:rgba(0,0,0,.6);color:#fff;cursor:pointer;transition:background .15s}
.mapstyle-line-style-btn:hover{background:rgba(0,0,0,.85)}
.mapstyle-line-style-btn .material-icons{font-size:15px;line-height:1}
.mapstyle-list > .position-relative:first-child {
  margin-left: 1.5em;
}
@media(max-width:475px){.mapstyle-card{width:108px;margin-right:.7em}}

.la-overlay-thumb {
  width: 100%;
  aspect-ratio: 4/3;
  background: linear-gradient(135deg, #4CAF50 0%, #2196F3 25%, #FF9800 50%, #AB47BC 75%, #FF5722 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}
/* Magenta, the overlay's dot colour (PUBLIC_PHOTOS_COLOR). The same shape as
   the other cards' 360×391 thumbnails, so the row lines up. */
.public-photos-thumb {
  aspect-ratio: 360 / 391;
  background: linear-gradient(135deg, #E91E8C 0%, #7B1FA2 100%);
}
.la-overlay-thumb .material-icons {
  font-size: 36px;
  color: rgba(255,255,255,0.9);
  text-shadow: 0 1px 4px rgba(0,0,0,0.3);
}
</style>
